import { NgClass } from "@angular/common";
import { AfterViewInit, Component, ElementRef, HostListener, Input, SecurityContext, ViewChild } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DataResponse, ProfileChanges, UpdateUserProfileRequest, UserProfile } from "@apiserver-rest/ponder-data-client-base";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { marked } from "marked";

@Component({
    selector: "profile-about-section",
    imports: [NgClass, BusySpinComponent],
    templateUrl: "./profile-about-section.component.html"
})
export class ProfileAboutSectionComponent implements AfterViewInit {
    @Input({ required: true }) public username!: string;
    @Input({ required: true }) public profile!: UserProfile;
    @Input({ required: true }) public isOwnProfile!: boolean;

    @ViewChild("aboutContainer") private aboutContainerRef!: ElementRef<HTMLDivElement>;
    @ViewChild("formattedAbout") private formattedContainerRef!: ElementRef<HTMLDivElement>;
    @ViewChild("editInput") private editInputRef!: ElementRef<HTMLTextAreaElement>;

    public raw!: string;
    public isEditing: boolean = false;
    public hasUnsavedChanges: boolean = false;
    public editedText?: string;
    public isSaving: boolean = false;
    public savingFailed: boolean = false;

    public readonly maxLength: number = 1024;
    public readonly warningLength: number = 900;
    public textLengthWarning: boolean = false;
    public textLengthError: boolean = false;

    constructor(private ponderDataClient: PonderDataClient, private domSanitizer: DomSanitizer) {
        
    }

    public ngAfterViewInit(): void {
        this.formatAboutText(this.profile.about);
    }

    private formatAboutText(text: string | undefined): void {
        this.raw = text?.trim() ?? "";

        if (this.raw) {
            var markdown = marked.parse(this.raw) as string;
            var sanitized = this.domSanitizer.sanitize(SecurityContext.HTML, markdown);

            if (sanitized) {
                this.formattedContainerRef.nativeElement.innerHTML = sanitized;
            }
        }
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.isEditing && !this.aboutContainerRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.isEditing && this.mouseDownOutside && !this.aboutContainerRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }

    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    public onEditClicked(): void {
        if (!this.isOwnProfile) {
            return;
        }

        this.isEditing = true;
        this.editedText = this.editedText ?? this.raw;

        if (this.editedText) {
            this.editInputRef.nativeElement.value = this.editedText;
        }
        
        this.updatedTextLengthWarning();
    }

    public onInputText(): void {
        this.editedText = this.editInputRef.nativeElement.value;
        this.updatedTextLengthWarning();
    }

    private updatedTextLengthWarning(): void {
        var l = this.editedText?.length ?? 0;

        if (l < this.warningLength) {
            this.textLengthWarning = false;
            this.textLengthError = false;
        } else {
            this.textLengthWarning = true;
            this.textLengthError = l > this.maxLength;
        }
    }

    public safeTextLength(): number {
        return this.editedText?.length ?? 0;
    }

    public onCancelClicked(): void {
        this.isEditing = false;
        this.editedText = undefined;
        this.hasUnsavedChanges = false;
    }

    public onSaveClicked(): void {
        if (this.isSaving || this.textLengthError) {
            return;
        }

        this.isSaving = true;
        this.savingFailed = false;

        this.editedText = this.editInputRef.nativeElement.value;

        if (this.editedText.localeCompare(this.raw) == 0) {
            this.isSaving = false;
            this.onCancelClicked();
            return;
        }

        var request = new UpdateUserProfileRequest({
            username: this.username,
            profileChanges: new ProfileChanges({
                about: this.editedText
            })
        });

        this.ponderDataClient.userProfilePatch(request).subscribe({
            next: (response: DataResponse) => {
                if (response.success) {
                    this.savingFailed = false;
                    this.formatAboutText(this.editedText);
                    this.onCancelClicked();
                } else {
                    this.savingFailed = true;
                }
            },
            error: () => {
                this.savingFailed = true;
            }
        }).add(() => {
            this.isSaving = false;
        });
    }

    public onClose(): void {
        if (this.isEditing) {
            this.isEditing = false;
            this.hasUnsavedChanges = (this.editedText ? true : false) && (this.editedText!.localeCompare(this.raw) != 0);
        }
    }
}